import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Volume2, VolumeX } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { scrollToId, IMAGES, CONTACT } from "@/lib/scroll";

const startRiver = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  const len = ctx.sampleRate * 4;
  const buffer = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3;
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.loop = true;
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 750;
  filter.Q.value = 0.6;
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.09;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 320;
  lfo.connect(lfoGain);
  lfoGain.connect(filter.frequency);
  const gain = ctx.createGain();
  gain.gain.value = 0;
  src.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  src.start();
  lfo.start();
  gain.gain.linearRampToValueAtTime(0.11, ctx.currentTime + 2.5);
  return { ctx, gain };
};

export const KineticHero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 180]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.2]);
  const riverRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);

  const toggleSound = () => {
    if (soundOn && riverRef.current) {
      const { ctx, gain } = riverRef.current;
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.2);
      setTimeout(() => ctx.close(), 1500);
      riverRef.current = null;
      setSoundOn(false);
    } else {
      riverRef.current = startRiver();
      setSoundOn(true);
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden bg-pawc-ink"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <img
          src={IMAGES.heroWater}
          alt=""
          className="w-full h-[120%] object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-pawc-ink/70 via-pawc-deep/40 to-pawc-ink/90" />
        <div className="absolute inset-0 bg-pawc-teal/20 mix-blend-multiply" />
      </motion.div>

      <div className="absolute inset-0 z-[5] overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute -top-[15%] -left-[10%] w-[55vw] h-[55vw] rounded-full bg-cyan-200/10 blur-3xl mix-blend-screen animate-caustic-a" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-sky-100/10 blur-3xl mix-blend-screen animate-caustic-b" />
        <div className="absolute top-[35%] left-[30%] w-[35vw] h-[35vw] rounded-full bg-pawc-mist/10 blur-3xl mix-blend-screen animate-caustic-a [animation-duration:38s]" />
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 w-full max-w-6xl mx-auto px-6 pb-24 pt-40"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="text-pawc-mist/80 text-xs md:text-sm tracking-[0.3em] uppercase mb-8"
          data-testid="hero-eyebrow"
        >
          {CONTACT.name} · {CONTACT.title}
        </motion.p>

        <h1 className="font-serif text-pawc-sand text-6xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-10">
          <MaskedLine delay={0.5}>Yielding</MaskedLine>
          <MaskedLine delay={0.7}>
            <em className="text-pawc-mist">Flow</em> {"&"} Form
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-xl text-pawc-sand/85 text-base md:text-lg leading-relaxed mb-10"
          data-testid="hero-welcome"
        >
          A calm, confidential space for young people and adults
          navigating trauma, emotional distress and life's transitions. Therapy
          that moves with you, the way water finds its path.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => scrollToId("#contact")}
            className="rounded-full bg-pawc-terra hover:bg-pawc-terraDark text-white px-8 py-4 text-sm tracking-wide transition-colors duration-300"
          >
            Book a Session
          </button>
          <button
            data-testid="hero-contact-btn"
            onClick={() => scrollToId("#about")}
            className="rounded-full border border-pawc-sand/40 hover:border-pawc-sand text-pawc-sand px-8 py-4 text-sm tracking-wide transition-colors duration-300"
          >
            Meet Anoshamisa
          </button>
        </motion.div>

        <motion.blockquote
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.8 }}
          className="mt-16 max-w-lg border-l-2 border-pawc-terra pl-6"
          data-testid="hero-quote"
        >
          <p className="font-serif italic text-xl md:text-2xl text-pawc-mist/90 leading-snug">
            "The only way to make sense out of change is to plunge into it, move
            with it, and join the dance."
          </p>
          <cite className="not-italic text-xs tracking-[0.25em] uppercase text-pawc-sand/60 mt-3 block">
            Alan Watts
          </cite>
        </motion.blockquote>
      </motion.div>

      <motion.button
        data-testid="hero-sound-toggle"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        onClick={toggleSound}
        aria-pressed={soundOn}
        aria-label={soundOn ? "Turn river sound off" : "Play gentle river sound"}
        className="absolute bottom-8 left-6 md:left-8 z-10 inline-flex items-center gap-2.5 rounded-full border border-pawc-sand/30 hover:border-pawc-sand/70 text-pawc-sand/80 px-5 py-2.5 text-xs tracking-[0.2em] uppercase backdrop-blur-sm bg-pawc-ink/20 transition-colors duration-300"
      >
        {soundOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
        {soundOn ? "River on" : "River sound"}
      </motion.button>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden md:flex flex-col items-center gap-3 text-pawc-sand/60"
        aria-hidden="true"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase rotate-90 origin-center translate-y-[-8px]">
          Scroll
        </span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
